import type {
  GooglePlaceDetailsV2,
  GooglePlacesAutocompleteResponseV2,
  LimitOffsetPaginatedResponse,
  ProviderDirectoryV2CreateServiceRequest,
  ProviderDirectoryV2CreateServiceResponse,
  ProviderDirectoryV2Location,
  ProviderDirectoryV2Organization,
  ProviderDirectoryV2SearchResponse,
  ProviderDirectoryV2Service,
} from '@/services/types/provider-directory-v2-types';

import { baseApiClient } from './baseService';

export const addTagTypes = [
  'ProviderDirectoryV2Search',
  'ProviderDirectoryV2Service',
  'ProviderDirectoryV2Location',
  'ProviderDirectoryV2Organization',
] as const;

const injectedApi = baseApiClient
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
    getProviderDirectoryOrganization: builder.query<
      ProviderDirectoryV2Organization,
      { organizationId: string }
    >({
      query: ({ organizationId }) =>
        `api/provider-directory/v2/organizations/${organizationId}/`,
      providesTags: (result, error, { organizationId }) => [
        { type: 'ProviderDirectoryV2Organization', id: organizationId },
      ],
    }),

    getProviderDirectoryOrganizations: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Organization>,
      { q?: string; limit?: number; offset?: number }
    >({
      query: ({ q, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (q) params.set('q', q);
        return `api/provider-directory/v2/organizations/?${params.toString()}`;
      },
      providesTags: [{ type: 'ProviderDirectoryV2Organization', id: 'LIST' }],
    }),

    getProviderDirectoryLocation: builder.query<
      ProviderDirectoryV2Location,
      { locationId: string }
    >({
      query: ({ locationId }) =>
        `api/provider-directory/v2/locations/${locationId}/`,
      providesTags: (result, error, { locationId }) => [
        { type: 'ProviderDirectoryV2Location', id: locationId },
      ],
    }),

    getProviderDirectoryOrganizationLocations: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Location>,
      { organizationId: string; limit?: number; offset?: number }
    >({
      query: ({ organizationId, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        return `api/provider-directory/v2/organizations/${organizationId}/locations/?${params.toString()}`;
      },
    }),

    getProviderDirectoryOrganizationServices: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Service>,
      { organizationId: string; limit?: number; offset?: number }
    >({
      query: ({ organizationId, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        return `api/provider-directory/v2/organizations/${organizationId}/services/?${params.toString()}`;
      },
    }),

    getProviderDirectoryLocations: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Location>,
      { q?: string; organization_id?: string; limit?: number; offset?: number }
    >({
      query: ({ q, organization_id, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (q) params.set('q', q);
        if (organization_id) params.set('organization_id', organization_id);
        return `api/provider-directory/v2/locations/?${params.toString()}`;
      },
      providesTags: [{ type: 'ProviderDirectoryV2Location', id: 'LIST' }],
    }),

    getProviderDirectoryDepartments: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Location>,
      { q?: string; limit?: number; offset?: number }
    >({
      query: ({ q, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (q) params.set('q', q);
        return `api/provider-directory/v2/departments/?${params.toString()}`;
      },
      providesTags: [{ type: 'ProviderDirectoryV2Location', id: 'LIST' }],
    }),

    getProviderDirectoryLocationServices: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Service>,
      { locationId: string; limit?: number; offset?: number }
    >({
      query: ({ locationId, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        return `api/provider-directory/v2/locations/${locationId}/services/?${params.toString()}`;
      },
    }),

    getProviderDirectoryService: builder.query<
      ProviderDirectoryV2Service,
      { serviceId: string }
    >({
      query: ({ serviceId }) =>
        `api/provider-directory/v2/services/${serviceId}/`,
      providesTags: (result, error, { serviceId }) => [
        { type: 'ProviderDirectoryV2Service', id: serviceId },
      ],
    }),

    getProviderDirectoryServices: builder.query<
      LimitOffsetPaginatedResponse<ProviderDirectoryV2Service>,
      {
        q?: string;
        limit?: number;
        offset?: number;
        service_type?: string;
        city?: string;
        state?: string;
        portal_eligible?: boolean;
        has_verified_contact?: boolean;
      }
    >({
      query: ({
        q,
        limit = 50,
        offset = 0,
        service_type,
        city,
        state,
        portal_eligible,
        has_verified_contact,
      }) => {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (q) params.set('q', q);
        if (service_type) params.set('service_type', service_type);
        if (city) params.set('city', city);
        if (state) params.set('state', state);
        if (portal_eligible != null)
          params.set('portal_eligible', String(portal_eligible));
        if (has_verified_contact != null)
          params.set('has_verified_contact', String(has_verified_contact));
        return `api/provider-directory/v2/services/?${params.toString()}`;
      },
      providesTags: [{ type: 'ProviderDirectoryV2Service', id: 'LIST' }],
    }),

    searchProviderServices: builder.query<
      ProviderDirectoryV2SearchResponse,
      { q: string; limit?: number; intent?: string; matter_id?: string }
    >({
      query: ({ q, limit = 10, intent, matter_id }) => {
        const params = new URLSearchParams();
        params.set('q', q);
        params.set('limit', String(limit));
        if (intent) params.set('intent', intent);
        if (matter_id) params.set('matter_id', matter_id);
        return `api/provider-directory/v2/search/?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [{ type: 'ProviderDirectoryV2Search', id: result.query }]
          : [{ type: 'ProviderDirectoryV2Search', id: 'UNKNOWN' }],
    }),

    googlePlacesAutocomplete: builder.query<
      GooglePlacesAutocompleteResponseV2,
      {
        q: string;
        session_token: string;
        center_lat?: number;
        center_lng?: number;
        radius_m?: number;
        strict_bounds?: boolean;
      }
    >({
      query: ({
        q,
        session_token,
        center_lat,
        center_lng,
        radius_m,
        strict_bounds,
      }) => {
        const params = new URLSearchParams();
        params.set('q', q);
        params.set('session_token', session_token);
        if (center_lat != null) params.set('center_lat', String(center_lat));
        if (center_lng != null) params.set('center_lng', String(center_lng));
        if (radius_m != null) params.set('radius_m', String(radius_m));
        if (strict_bounds != null)
          params.set('strict_bounds', strict_bounds ? 'true' : 'false');
        return `api/provider-directory/v2/google/places/autocomplete/?${params.toString()}`;
      },
    }),

    googlePlacesDetails: builder.query<
      GooglePlaceDetailsV2,
      { place_id: string; session_token: string }
    >({
      query: ({ place_id, session_token }) => {
        const params = new URLSearchParams();
        params.set('place_id', place_id);
        params.set('session_token', session_token);
        return `api/provider-directory/v2/google/places/details/?${params.toString()}`;
      },
    }),

    createProviderService: builder.mutation<
      ProviderDirectoryV2CreateServiceResponse,
      ProviderDirectoryV2CreateServiceRequest
    >({
      query: (body) => ({
        url: `api/provider-directory/v2/provider-services/create/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'ProviderDirectoryV2Service', id: 'LIST' },
        { type: 'ProviderDirectoryV2Location', id: 'LIST' },
      ],
    }),

    createProviderDirectoryServiceFromDirectory: builder.mutation<
      {
        provider_location_id: string;
        provider_service_id: string;
        reused_existing: boolean;
      },
      {
        source: 'internal_existing' | 'google_place' | 'manual';
        base_provider_service_id?: string | null;
        service_name: string;
        service_type?: string | null;
        address: {
          formatted_address?: string | null;
          address_line1?: string | null;
          address_line2?: string | null;
          city?: string | null;
          state?: string | null;
          postal_code?: string | null;
          country?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          google_place_id?: string | null;
        };
        phone?: string | null;
        website?: string | null;
      }
    >({
      query: (body) => ({
        url: `api/provider-directory/v2/directory-services/create/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'ProviderDirectoryV2Service', id: 'LIST' },
        { type: 'ProviderDirectoryV2Location', id: 'LIST' },
      ],
    }),

    updateProviderDirectoryService: builder.mutation<
      ProviderDirectoryV2Service,
      {
        serviceId: string;
        patch: {
          name?: string;
          service_type?: string | null;
          aliases?: string[] | null;
          portal_eligible?: boolean | null;
          portal_type?: string[] | null;
          notes?: string | null;
          clinical_location_id?: string;
          billing_location_id?: string | null;
          records_location_id?: string | null;
        };
      }
    >({
      query: ({ serviceId, patch }) => ({
        url: `api/provider-directory/v2/services/${serviceId}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { serviceId }) => [
        { type: 'ProviderDirectoryV2Service', id: serviceId },
        { type: 'ProviderDirectoryV2Service', id: 'LIST' },
      ],
    }),

    createProviderDirectoryOrganization: builder.mutation<
      ProviderDirectoryV2Organization,
      Pick<
        ProviderDirectoryV2Organization,
        'name' | 'organization_type' | 'aliases' | 'website'
      >
    >({
      query: (body) => ({
        url: `api/provider-directory/v2/organizations/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'ProviderDirectoryV2Organization', id: 'LIST' },
      ],
    }),

    updateProviderDirectoryOrganization: builder.mutation<
      ProviderDirectoryV2Organization,
      {
        organizationId: string;
        patch: Partial<
          Pick<
            ProviderDirectoryV2Organization,
            'name' | 'organization_type' | 'aliases' | 'website'
          >
        >;
      }
    >({
      query: ({ organizationId, patch }) => ({
        url: `api/provider-directory/v2/organizations/${organizationId}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (r, e, { organizationId }) => [
        { type: 'ProviderDirectoryV2Organization', id: organizationId },
        { type: 'ProviderDirectoryV2Organization', id: 'LIST' },
      ],
    }),

    createProviderDirectoryLocation: builder.mutation<
      ProviderDirectoryV2Location,
      Pick<
        ProviderDirectoryV2Location,
        'name' | 'location_type' | 'status' | 'notes' | 'organization_id'
      >
    >({
      query: (body) => ({
        url: `api/provider-directory/v2/locations/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ProviderDirectoryV2Location', id: 'LIST' }],
    }),

    updateProviderDirectoryLocation: builder.mutation<
      ProviderDirectoryV2Location,
      {
        locationId: string;
        patch: Partial<
          Pick<
            ProviderDirectoryV2Location,
            'name' | 'location_type' | 'status' | 'notes' | 'organization_id'
          >
        >;
      }
    >({
      query: ({ locationId, patch }) => ({
        url: `api/provider-directory/v2/locations/${locationId}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (r, e, { locationId }) => [
        { type: 'ProviderDirectoryV2Location', id: locationId },
        { type: 'ProviderDirectoryV2Location', id: 'LIST' },
      ],
    }),

    mergeProviderDirectoryLocations: builder.mutation<
      {
        canonical_location_id: string;
        merged_location_ids: string[];
        affected_service_ids: string[];
        moved_points: number;
        archived_points: number;
        archived_locations: number;
        archived_cards: number;
        dry_run: boolean;
      },
      {
        canonical_location_id: string;
        merge_location_ids: string[];
        dry_run?: boolean;
      }
    >({
      query: (body) => ({
        url: `api/provider-directory/v2/merge/locations/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'ProviderDirectoryV2Service', id: 'LIST' },
        { type: 'ProviderDirectoryV2Location', id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export { injectedApi as providerDirectoryV2Api };

export const {
  useGetProviderDirectoryOrganizationQuery,
  useGetProviderDirectoryOrganizationsQuery,
  useGetProviderDirectoryLocationQuery,
  useGetProviderDirectoryOrganizationLocationsQuery,
  useGetProviderDirectoryOrganizationServicesQuery,
  useGetProviderDirectoryLocationsQuery,
  useGetProviderDirectoryDepartmentsQuery,
  useGetProviderDirectoryLocationServicesQuery,
  useGetProviderDirectoryServiceQuery,
  useGetProviderDirectoryServicesQuery,
  useSearchProviderServicesQuery,
  useLazySearchProviderServicesQuery,
  useGooglePlacesAutocompleteQuery,
  useLazyGooglePlacesAutocompleteQuery,
  useLazyGooglePlacesDetailsQuery,
  useCreateProviderServiceMutation,
  useCreateProviderDirectoryServiceFromDirectoryMutation,
  useUpdateProviderDirectoryServiceMutation,
  useCreateProviderDirectoryOrganizationMutation,
  useUpdateProviderDirectoryOrganizationMutation,
  useCreateProviderDirectoryLocationMutation,
  useUpdateProviderDirectoryLocationMutation,
  useMergeProviderDirectoryLocationsMutation,
} = injectedApi;
